import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, Plus, Star, Eye, Download, QrCode, FileImage } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Input, Textarea } from '../UI/Input';
import { InviteLinkPanel } from './InviteLinkPanel';
import { PosterModal } from './PosterModal';
import { A4PosterModal } from './A4PosterModal';
import { OverlayConfigCard } from './OverlayConfigCard';
import { AlbumPhotoCanvas, AlbumPhotoCanvasHandle } from './AlbumPhotoCanvas';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { albumsApi } from '../../api/albums';
import { photosApi } from '../../api/photos';
import { overlaysApi } from '../../api/overlays';
import { eventsApi } from '../../api/events';
import { Album, AlbumOverlayConfig, OverlayAnchor, Photo, Overlay, Event } from '../../types';
import { track, EVENTS } from '../../lib/mixpanel';
import { QuotaLimitModal } from '../UI/QuotaLimitModal';
import { useQuotaGuard } from '../../hooks/useQuotaGuard';
import { brandingApi } from '../../api/settings';
import { getReservedAnchors, pickFreeAnchor } from '../../lib/overlayAnchors';
import { useAuthStore } from '../../store/authStore';
import { FaceRecognitionStep, FaceRecognitionConfig } from './FaceRecognitionStep';
import { DomainRestrictionField } from './DomainRestrictionField';
import styles from './AlbumWizard.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AlbumWizardProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  onCreated: (album: Album) => void;
}

type WizardStep = 1 | 2 | 3 | 4 | 5 | 6;

// ─── Step stepper with names ──────────────────────────────────────────────────

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Info',
  2: 'Photos',
  3: 'Cover',
  4: 'Face',
  5: 'Overlays',
  6: 'Share',
};

function StepDots({ step }: { step: WizardStep }) {
  return (
    <div className={styles['step-dots']}>
      {([1, 2, 3, 4, 5, 6] as const).map((n, i) => {
        const done = step > n;
        const active = step === n;
        return (
          <React.Fragment key={n}>
            <div className={styles['step-dot-col']}>
              <div
                className={[
                  styles['step-dot'],
                  done ? styles['step-dot-done'] : active ? styles['step-dot-active'] : styles['step-dot-inactive'],
                ].join(' ')}
              >
                {done ? <Check className={styles['icon-md']} strokeWidth={3} /> : n}
              </div>
              <span
                className={[
                  styles['step-label'],
                  active ? styles['step-label-active'] : done ? styles['step-label-done'] : styles['step-label-inactive'],
                ].join(' ')}
              >
                {STEP_LABELS[n]}
              </span>
            </div>

            {i < 5 && (
              <div
                className={[
                  styles['step-connector'],
                  step > n ? styles['step-connector-done'] : styles['step-connector-pending'],
                ].join(' ')}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Info ─────────────────────────────────────────────────────────────

function Step1({
  name, description, allowedEmailDomains,
  onChange, onDomainsChange,
  onNext, onCancel,
}: {
  name: string; description: string; allowedEmailDomains: string[];
  onChange: (name: string, description: string) => void;
  onDomainsChange: (domains: string[]) => void;
  onNext: () => void; onCancel: () => void;
}) {
  return (
    <div className={styles['step-col']}>
      <div className={styles['step-scroll']}>
        <Input
          label="Album Name"
          placeholder="e.g. Wedding Day Highlights"
          value={name}
          onChange={(e) => onChange(e.target.value, description)}
          maxLength={80}
          required
          autoFocus
        />
        <Textarea
          label="Description (optional)"
          placeholder="A note for your guests..."
          value={description}
          onChange={(e) => onChange(name, e.target.value)}
          rows={3}
        />
        <DomainRestrictionField value={allowedEmailDomains} onChange={onDomainsChange} />
      </div>
      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={onNext} disabled={!name.trim()}>
          Next
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2: Photo picker ─────────────────────────────────────────────────────

function Step2({
  eventId,
  selectedIds,
  onToggle,
  onRangeToggle,
  onSelectAll,
  onPhotosLoaded,
  onNext, onBack,
}: {
  eventId: string;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onRangeToggle: (ids: string[], select: boolean) => void;
  onSelectAll: (photos: Photo[], selectAll: boolean) => void;
  onPhotosLoaded: (photos: Photo[]) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const lastClickedIndexRef = useRef<number>(-1);

  // Notify parent of loaded photos
  const updatePhotos = useCallback((newPhotos: Photo[]) => {
    setPhotos(newPhotos);
    onPhotosLoaded(newPhotos);
  }, [onPhotosLoaded]);

  useEffect(() => {
    if (initialized) return;
    setLoading(true);
    photosApi.list(eventId)
      .then((r) => {
        updatePhotos(r.photos);
        setNextCursor(r.nextCursor);
        setHasMore(r.hasMore);
        setInitialized(true);
      })
      .catch(() => toast.error('Failed to load photos'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await photosApi.list(eventId, nextCursor);
      const merged = [...photos, ...r.photos];
      updatePhotos(merged);
      setNextCursor(r.nextCursor);
      setHasMore(r.hasMore);
    } catch {
      toast.error('Failed to load more photos');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, eventId, photos, updatePhotos]);

  // Infinite scroll via IntersectionObserver on sentinel
  const loadMoreRef = useRef(loadMore);
  useEffect(() => { loadMoreRef.current = loadMore; }, [loadMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current(); },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialized]);

  // Select All: if there are unloaded pages, fetch them all first, then select
  const handleSelectAll = async () => {
    const allCurrentlySelected = photos.length > 0 && photos.every((p) => selectedIds.has(p._id));

    if (allCurrentlySelected && !hasMore) {
      // Deselect all
      onSelectAll([], false);
      return;
    }

    if (!hasMore) {
      // All loaded — toggle select/deselect
      if (allCurrentlySelected) {
        onSelectAll([], false);
      } else {
        onSelectAll(photos, true);
      }
      return;
    }

    // Need to load all remaining pages first
    setSelectingAll(true);
    let allPhotos = [...photos];
    let cursor = nextCursor;
    try {
      while (cursor) {
        const r = await photosApi.list(eventId, cursor);
        allPhotos = [...allPhotos, ...r.photos];
        cursor = r.nextCursor ?? null;
        setNextCursor(r.nextCursor);
        setHasMore(r.hasMore);
      }
      updatePhotos(allPhotos);
      onSelectAll(allPhotos, true);
    } catch {
      toast.error('Failed to load all photos');
    } finally {
      setSelectingAll(false);
    }
  };

  const allSelected = photos.length > 0 && photos.every((p) => selectedIds.has(p._id)) && !hasMore;
  const someSelected = selectedIds.size > 0;
  const busyAll = selectingAll || loadingMore;

  return (
    <div className={styles['picker-col']}>

      {/* Toolbar */}
      <div className={styles['picker-toolbar']}>
        <button
          onClick={handleSelectAll}
          disabled={busyAll || loading}
          className={styles['select-all-btn']}
        >
          <div className={[
            styles['checkbox-box'],
            allSelected ? styles['checkbox-all'] : someSelected ? styles['checkbox-some'] : '',
          ].filter(Boolean).join(' ')}>
            {allSelected && <Check className={`${styles['icon-xs']} ${styles['icon-white']}`} strokeWidth={3} />}
            {!allSelected && someSelected && <div className={styles['checkbox-dash']} />}
          </div>
          {selectingAll ? 'Loading all\u2026' : allSelected ? 'Deselect All' : 'Select All'}
        </button>
        <span className={styles['selected-count']}>
          {selectedIds.size} selected{hasMore ? ' (more to load)' : ''}
        </span>
      </div>

      {/* Grid */}
      <div className={styles['grid-scroll']}>
        {loading ? (
          <div className={styles['photo-grid']}>
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className={styles['skeleton-cell']} />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className={styles['empty-state']}>
            <MachhaCharacter state="Empty" size={100} />
            <p className={styles['empty-text']}>
              No photos uploaded yet
            </p>
          </div>
        ) : (
          <>
            <div className={styles['photo-grid']}>
              {photos.map((photo, idx) => {
                const selected = selectedIds.has(photo._id);
                return (
                  <button
                    key={photo._id}
                    onClick={(e) => {
                      if (e.shiftKey && lastClickedIndexRef.current >= 0) {
                        const from = Math.min(lastClickedIndexRef.current, idx);
                        const to = Math.max(lastClickedIndexRef.current, idx);
                        const rangeIds = photos.slice(from, to + 1).map((p) => p._id);
                        const shouldSelect = !selected;
                        onRangeToggle(rangeIds, shouldSelect);
                      } else {
                        onToggle(photo._id);
                      }
                      lastClickedIndexRef.current = idx;
                    }}
                    aria-label={`${selected ? 'Deselect' : 'Select'} ${photo.originalFilename}`}
                    aria-pressed={selected}
                    className={[
                      styles['photo-cell'],
                      selected ? styles['photo-cell-selected'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {photo.thumbnailUrl ? (
                      <img
                        src={photo.thumbnailUrl}
                        alt={photo.originalFilename}
                        className={styles['img-cover']}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles['img-placeholder']} />
                    )}
                    {selected && (
                      <div className={styles['check-badge']}>
                        <Check className={`${styles['icon-sm']} ${styles['icon-white']}`} strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className={styles['sentinel']} />

            {loadingMore && (
              <div className={styles['loading-dots']}>
                <div className={styles['loading-dots-inner']}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`${styles['bounce-dot']} ${styles['bounce-dot-delay']}`}
                      style={{ '--bounce-delay': `${i * 0.15}s` } as React.CSSProperties}
                    />
                  ))}
                </div>
              </div>
            )}

            {!hasMore && initialized && photos.length > 0 && (
              <p className={styles['end-msg']}>
                — that's all the photos —
              </p>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className={styles['step-footer-split']}>
        <Button variant="ghost" onClick={onBack}>&larr; Back</Button>
        <Button onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: Cover photo picker ───────────────────────────────────────────────

function StepCover({
  selectedPhotos,
  coverPhotoId,
  onSetCover,
  onNext,
  onBack,
}: {
  selectedPhotos: Photo[];
  coverPhotoId: string | null;
  onSetCover: (id: string | null) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <div className={styles['step-col']}>
      <p className={styles['cover-hint']}>
        Pick a cover photo shown (blurred) to guests on the join page.
        Click the same photo again to clear.
      </p>

      <div className={styles['cover-scroll']}>
        {selectedPhotos.length === 0 ? (
          <div className={styles['empty-state']}>
            <MachhaCharacter state="Empty" size={80} />
            <p className={styles['empty-text']}>
              No photos selected — you can skip this step.
            </p>
          </div>
        ) : (
          <div className={styles['cover-grid']}>
            {selectedPhotos.map((photo) => {
              const isCover = coverPhotoId === photo._id;
              return (
                <button
                  key={photo._id}
                  onClick={() => onSetCover(isCover ? null : photo._id)}
                  aria-label={isCover ? `Remove cover: ${photo.originalFilename}` : `Set as cover: ${photo.originalFilename}`}
                  aria-pressed={isCover}
                  className={[
                    styles['cover-cell'],
                    isCover ? styles['cover-cell-active'] : '',
                  ].filter(Boolean).join(' ')}
                >
                  {photo.thumbnailUrl ? (
                    <img
                      src={photo.thumbnailUrl}
                      alt={photo.originalFilename}
                      className={styles['img-cover']}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles['img-placeholder']} />
                  )}

                  {/* Cover overlay */}
                  {isCover && (
                    <div className={styles['cover-overlay']}>
                      <div className={styles['cover-badge']}>
                        <Star className={`${styles['icon-xs']} ${styles['icon-white-filled']}`} />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {coverPhotoId && (
        <p className={styles['cover-set-msg']}>
          &#9733; Cover photo set
          <button
            onClick={() => onSetCover(null)}
            className={styles['cover-clear-btn']}
          >
            Clear
          </button>
        </p>
      )}

      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onBack}>&larr; Back</Button>
        <Button onClick={onNext}>Next</Button>
      </div>
    </div>
  );
}

// ─── Overlay preview — full-screen overlay ────────────────────────────────────

function OverlayPreviewModal({
  isOpen,
  onClose,
  photo,
  overlayConfigs,
}: {
  isOpen: boolean;
  onClose: () => void;
  photo: Photo;
  overlayConfigs: AlbumOverlayConfig[];
}) {
  const canvasRef = useRef<AlbumPhotoCanvasHandle>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  // Natural aspect ratio of the underlying photo (width / height). Loaded
  // asynchronously from the thumbnail so we can size the canvas correctly
  // and avoid cropping or letterbox bars.
  const [imgAspect, setImgAspect] = useState<number | null>(null);

  // Probe the photo's natural dimensions when the modal opens
  useEffect(() => {
    if (!isOpen) return;
    const url = photo.thumbnailUrl ?? photo.originalUrl ?? '';
    if (!url) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setImgAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = url;
    return () => { cancelled = true; };
  }, [isOpen, photo.thumbnailUrl, photo.originalUrl]);

  // Compute canvas size from the image's natural aspect, capped to viewport
  useEffect(() => {
    if (!isOpen) return;
    const measure = () => {
      const vw = Math.min(window.innerWidth * 0.9, 1200);
      const vh = window.innerHeight * 0.75;
      // Until the image loads we don't know its aspect — fall back to 3:2,
      // which is the most common camera output. Once `imgAspect` arrives the
      // effect re-runs and resizes precisely to fit the photo.
      const aspect = imgAspect ?? 3 / 2;
      let w = vw;
      let h = w / aspect;
      if (h > vh) { h = vh; w = h * aspect; }
      setSize({ w: Math.round(w), h: Math.round(h) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen, imgAspect]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Overlay Preview" size="xl">
      <div className={styles['preview-body']}>
        {/* Canvas */}
        <div className={styles['preview-canvas-wrap']}>
          {size.w > 0 && (
            <AlbumPhotoCanvas
              ref={canvasRef}
              photo={photo}
              overlayConfigs={overlayConfigs}
              width={size.w}
              height={size.h}
              useOriginal={false}
              fit="contain"
            />
          )}
        </div>

        {/* Action bar */}
        <div className={styles['preview-actions']}>
          <p className={styles['preview-caption']}>
            This is how the overlay will appear on photos in your album.
          </p>
          <button
            onClick={() => canvasRef.current?.downloadCanvas()}
            className={styles['preview-action-btn']}
          >
            <Download className={styles['icon-md']} />
            Save Preview
          </button>
          <button
            onClick={onClose}
            className={styles['preview-action-btn']}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Step 3: Overlay config ───────────────────────────────────────────────────

function Step3({
  eventId,
  overlayConfigs,
  previewPhoto,
  onAdd,
  onUpdate,
  onRemove,
  onSubmit,
  onBack,
  submitting,
  submitError,
  isOwner,
  attachDefaultOverlay,
  onToggleAttachDefault,
  defaultBranded,
  defaultAnchor,
}: {
  eventId: string;
  overlayConfigs: AlbumOverlayConfig[];
  previewPhoto: Photo | null;
  onAdd: (overlay: Overlay) => void;
  onUpdate: (index: number, updated: AlbumOverlayConfig) => void;
  onRemove: (index: number) => void;
  onSubmit: () => void;
  onBack: () => void;
  submitting: boolean;
  submitError: string;
  isOwner: boolean;
  attachDefaultOverlay: boolean;
  onToggleAttachDefault: (value: boolean) => void;
  defaultBranded: boolean;
  defaultAnchor: OverlayAnchor | null;
}) {
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [loadingOverlays, setLoadingOverlays] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (initialized) return;
    setLoadingOverlays(true);
    overlaysApi.list(eventId)
      .then((data) => {
        setOverlays(data);
        setInitialized(true);
      })
      .catch(() => toast.error('Failed to load overlays'))
      .finally(() => setLoadingOverlays(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const addedOverlayIds = new Set(overlayConfigs.map((c) => c.overlayId));
  const availableToAdd = overlays.filter((o) => !addedOverlayIds.has(o._id));

  return (
    <div className={styles['step-col']}>
      <div className={styles['step-scroll']}>
        {/* Add overlay strip */}
        {loadingOverlays ? (
          <div className={styles['overlay-skeleton-row']}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles['overlay-skeleton-item']} />
            ))}
          </div>
        ) : overlays.length === 0 ? (
          <div className={styles['overlay-empty-msg']}>
            No overlays uploaded for this event. Add overlays in the Overlays tab first.
          </div>
        ) : availableToAdd.length > 0 ? (
          <div>
            <p className={styles['overlay-add-label']}>Add Overlay</p>
            <div className={styles['overlay-strip']}>
              {availableToAdd.map((overlay) => (
                <button
                  key={overlay._id}
                  onClick={() => onAdd(overlay)}
                  aria-label={`Add overlay ${overlay.name}`}
                  className={styles['overlay-add-btn']}
                >
                  <div className={styles['overlay-add-thumb']}>
                    <img
                      src={overlaysApi.fileUrl(overlay._id)}
                      alt={overlay.name}
                      className={styles['img-contain']}
                    />
                    <div className={styles['overlay-add-hover-mask']}>
                      <Plus className={`${styles['icon-lg']} ${styles['icon-clay-orange']}`} />
                    </div>
                  </div>
                  <span className={styles['overlay-add-name']}>
                    {overlay.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Configured overlays */}
        {overlayConfigs.length === 0 ? (
          <div className={styles['overlay-none-msg']}>
            No overlays added — photos will be shown without overlays
          </div>
        ) : (
          <div className={styles['overlay-configs-col']}>
            {overlayConfigs.map((config, i) => {
              const overlay = overlays.find((o) => o._id === config.overlayId);
              if (!overlay) return null;
              const reservedAnchors = getReservedAnchors(overlayConfigs, i, {
                defaultBranded,
                defaultAnchor: defaultAnchor ?? undefined,
              });
              return (
                <OverlayConfigCard
                  key={config.overlayId}
                  overlay={overlay}
                  config={config}
                  reservedAnchors={reservedAnchors}
                  onChange={(updated) => onUpdate(i, updated)}
                  onRemove={() => onRemove(i)}
                />
              );
            })}
          </div>
        )}

        {/* Watermark disclaimer / owner opt-in checkbox */}
        {isOwner ? (
          <label className={styles['watermark-box']} style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={attachDefaultOverlay}
              onChange={(e) => onToggleAttachDefault(e.target.checked)}
              style={{ marginTop: '0.125rem', accentColor: 'var(--clay-orange)' }}
              aria-label="Attach default Pixel Machha branding watermark"
            />
            <p className={styles['watermark-text']}>
              Attach the <strong>default Pixel Machha branding watermark</strong> to every photo in this album.
              As an owner you can untick this to skip the default branding for this album only.
            </p>
          </label>
        ) : (
          <div className={styles['watermark-box']}>
            <span className={styles['watermark-icon']}>&#9888;</span>
            <p className={styles['watermark-text']}>
              A <strong>Pixel Machha watermark</strong> is automatically applied to all album photos during beta.
              You may request its removal by contacting us.
              Unauthorized removal and distribution of unwatermarked photos on any platform is prohibited and may result in legal action.
            </p>
          </div>
        )}

        {submitError && (
          <p className={styles['submit-error']}>
            {submitError}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>&larr; Back</Button>
        <div className={styles['footer-btn-group']}>
          {overlayConfigs.length > 0 && previewPhoto && (
            <Button
              variant="ghost"
              onClick={() => setShowPreview(true)}
              disabled={submitting}
            >
              <Eye className={styles['icon-eye-preview']} />
              Preview
            </Button>
          )}
          <Button onClick={onSubmit} loading={submitting}>
            Create Album
          </Button>
        </div>
      </div>

      {/* Overlay preview modal */}
      {previewPhoto && (
        <OverlayPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          photo={previewPhoto}
          overlayConfigs={overlayConfigs}
        />
      )}
    </div>
  );
}

// ─── Step 4: Share link ───────────────────────────────────────────────────────

function Step4({
  album,
  event,
  coverPhoto,
  overlayConfigs,
  onAlbumUpdated,
  onDone,
}: {
  album: Album;
  event: Event | null;
  coverPhoto: Photo | null;
  overlayConfigs: AlbumOverlayConfig[];
  onAlbumUpdated: (updated: Album) => void;
  onDone: () => void;
}) {
  const shouldReduceMotion = useReducedMotion();
  const coverContainerRef = useRef<HTMLDivElement>(null);
  const [coverSize, setCoverSize] = useState(0);
  const [showPoster, setShowPoster] = useState(false);
  const [showA4Poster, setShowA4Poster] = useState(false);

  // Measure cover container width for responsive Konva canvas
  useEffect(() => {
    const el = coverContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      if (w > 0) setCoverSize(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={styles['share-col']}>
      {/* Album preview card with cover photo */}
      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={shouldReduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 26 }}
        className={styles['album-preview-card']}
      >
        {/* Cover photo or gradient fallback */}
        <div
          ref={coverContainerRef}
          className={`${styles['cover-container']} ${styles['cover-aspect']}`}
        >
          {coverPhoto && coverSize > 0 ? (
            <AlbumPhotoCanvas
              photo={coverPhoto}
              overlayConfigs={overlayConfigs}
              width={coverSize}
              height={Math.round(coverSize * 7 / 16)}
              useOriginal={false}
            />
          ) : (
            <div className={styles['cover-gradient-fallback']} />
          )}
          {/* Success badge */}
          <div className={styles['created-badge']}>
            <Check className={styles['icon-xs']} strokeWidth={3} />
            Created
          </div>
        </div>

        <div className={styles['card-body']}>
          {/* Album name + description */}
          <p className={styles['card-title']}>
            {album.name}
          </p>
          {album.description && (
            <p className={styles['card-desc']}>
              {album.description}
            </p>
          )}

          {/* Event label */}
          {event ? (
            <div className={styles['event-label-row']}>
              <span className={styles['event-label-prefix']}>
                Event
              </span>
              <span className={styles['event-label-value']}>
                {event.name}{event.description ? ` \u00b7 ${event.description}` : ''}
              </span>
            </div>
          ) : (
            <div className={styles['event-skeleton']} />
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={shouldReduceMotion ? { duration: 0 } : { delay: 0.1, duration: 0.2, ease: 'easeOut' }}
      >
        <InviteLinkPanel album={album} onTokenRegenerated={onAlbumUpdated} />
      </motion.div>

      <div className={styles['step-footer-end']}>
        <div className={styles['footer-btn-group']}>
          <Button variant="secondary" onClick={() => setShowPoster(true)}>
            <QrCode className={styles['icon-md']} />
            View QR Code
          </Button>
          <Button variant="secondary" onClick={() => setShowA4Poster(true)}>
            <FileImage className={styles['icon-md']} />
            Download Poster
          </Button>
          <Button onClick={onDone}>Done</Button>
        </div>
      </div>

      <PosterModal
        isOpen={showPoster}
        onClose={() => setShowPoster(false)}
        albumName={album.name}
        eventName={event?.name ?? ''}
        inviteToken={album.inviteToken}
      />

      <A4PosterModal
        isOpen={showA4Poster}
        onClose={() => setShowA4Poster(false)}
        albumId={album._id}
        albumName={album.name}
        eventName={event?.name ?? ''}
        inviteToken={album.inviteToken}
      />
    </div>
  );
}

// ─── Wizard root ──────────────────────────────────────────────────────────────

const DEFAULT_OVERLAY_CONFIG = (anchor: OverlayAnchor = 'bottom-right'): Omit<AlbumOverlayConfig, 'overlayId'> => ({
  anchor,
  size: 'md',
  opacity: 1,
});

export function AlbumWizard({ isOpen, onClose, eventId, onCreated }: AlbumWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const shouldReduceMotion = useReducedMotion();
  const { quotaModal, handleQuotaError, closeQuotaModal } = useQuotaGuard();
  const isOwner = useAuthStore((s) => s.user?.isOwner ?? false);

  // Step 1
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allowedEmailDomains, setAllowedEmailDomains] = useState<string[]>([]);

  // Step 2
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const [coverPhotoId, setCoverPhotoId] = useState<string | null>(null);
  const [loadedPhotos, setLoadedPhotos] = useState<Photo[]>([]);

  // Step 4 — Face Recognition
  const [faceConfig, setFaceConfig] = useState<FaceRecognitionConfig>({
    faceRecognitionEnabled: false,
    guestVisibility: 'all',
    faceSearchScope: 'album',
  });

  // Step 5 — Overlays
  const [overlayConfigs, setOverlayConfigs] = useState<AlbumOverlayConfig[]>([]);
  // Owner-only opt-in for the system default branding watermark.
  // Pre-checked: owners get the watermark by default but can untick to skip
  // it for an individual album. Non-owners always receive it server-side.
  const [attachDefaultOverlay, setAttachDefaultOverlay] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Step 6
  const [createdAlbum, setCreatedAlbum] = useState<Album | null>(null);
  const [event, setEvent] = useState<Event | null>(null);

  // System default branding overlay config (for the reserved-anchor logic)
  const [defaultAnchor, setDefaultAnchor] = useState<OverlayAnchor | null>(null);

  // Fetch event for Step 4 preview
  useEffect(() => {
    if (!isOpen || event) return;
    eventsApi.get(eventId).then(setEvent).catch(() => {/* non-critical */});
  }, [isOpen, eventId, event]);

  // Fetch the system default branding overlay anchor once per session
  useEffect(() => {
    if (!isOpen || defaultAnchor) return;
    brandingApi.getDefaults()
      .then((d) => setDefaultAnchor(d.config.anchor))
      .catch(() => {/* non-critical — disabled-anchor logic just won't kick in */});
  }, [isOpen, defaultAnchor]);

  // The system watermark will end up on this album if either:
  //   - the user is a non-owner (server always injects it)
  //   - the user is an owner who has opted in via the checkbox
  const defaultBranded = !isOwner || attachDefaultOverlay;

  const reset = useCallback(() => {
    setStep(1);
    setName('');
    setDescription('');
    setAllowedEmailDomains([]);
    setSelectedPhotoIds(new Set());
    setCoverPhotoId(null);
    setLoadedPhotos([]);
    setFaceConfig({ faceRecognitionEnabled: false, guestVisibility: 'all', faceSearchScope: 'album' });
    setOverlayConfigs([]);
    setAttachDefaultOverlay(true);
    setSubmitting(false);
    setSubmitError('');
    setCreatedAlbum(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // ── Step 2 helpers ──────────────────────────────────────────────────────────

  const togglePhoto = (id: string) =>
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const rangeTogglePhotos = (ids: string[], select: boolean) =>
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (select) next.add(id); else next.delete(id);
      }
      return next;
    });

  const handleSelectAll = (photos: Photo[], select: boolean) => {
    if (!select) {
      setSelectedPhotoIds(new Set());
    } else {
      setSelectedPhotoIds(new Set(photos.map((p) => p._id)));
    }
  };

  // ── Step 3 helpers ──────────────────────────────────────────────────────────

  const addOverlay = (overlay: Overlay) =>
    setOverlayConfigs((prev) => {
      const reserved = getReservedAnchors(prev, -1, {
        defaultBranded,
        defaultAnchor: defaultAnchor ?? undefined,
      });
      const anchor = pickFreeAnchor(reserved);
      return [
        ...prev,
        { overlayId: overlay._id, ...DEFAULT_OVERLAY_CONFIG(anchor) },
      ];
    });

  const updateOverlayConfig = (index: number, updated: AlbumOverlayConfig) =>
    setOverlayConfigs((prev) => prev.map((c, i) => (i === index ? updated : c)));

  const removeOverlayConfig = (index: number) =>
    setOverlayConfigs((prev) => prev.filter((_, i) => i !== index));

  // Pick a preview photo: cover first, then first selected, then first loaded
  const previewPhoto: Photo | null =
    loadedPhotos.find((p) => p._id === coverPhotoId) ??
    loadedPhotos.find((p) => selectedPhotoIds.has(p._id)) ??
    loadedPhotos[0] ??
    null;

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      let album = await albumsApi.create({
        name: name.trim(),
        description: description.trim(),
        eventId,
        ...(coverPhotoId && { coverPhotoId }),
        ...(allowedEmailDomains.length > 0 && { allowedEmailDomains }),
        // Owner opt-in. Non-owners always get the default server-side regardless.
        ...(isOwner && { attachDefaultOverlay }),
        // Face recognition
        faceRecognitionEnabled: faceConfig.faceRecognitionEnabled,
        ...(faceConfig.faceRecognitionEnabled && {
          guestVisibility: faceConfig.guestVisibility,
          faceSearchScope: faceConfig.faceSearchScope,
        }),
      });

      if (selectedPhotoIds.size > 0) {
        await albumsApi.addPhotos(album._id, [...selectedPhotoIds]);
      }

      if (overlayConfigs.length > 0) {
        // Merge any server-injected default-branding entries (created above) with
        // the user's manual overlay selections, so calling `update` doesn't
        // accidentally drop the default. The wizard's manual overlay strip never
        // surfaces the default to the user, so duplicates aren't a concern here.
        const manualOverlayIds = new Set(overlayConfigs.map((c) => c.overlayId));
        const injectedDefaults = album.overlayConfigs.filter(
          (c) => !manualOverlayIds.has(c.overlayId),
        );
        const merged = [...injectedDefaults, ...overlayConfigs];
        album = await albumsApi.update(album._id, { overlayConfigs: merged });
      }

      // Re-fetch to get the complete album state (coverPhotoId + overlayConfigs + photos)
      try { album = await albumsApi.get(album._id); } catch { /* use last known */ }

      track(EVENTS.ALBUM_CREATED, {
        album_id: album._id,
        album_name: album.name,
        photo_count: selectedPhotoIds.size,
        overlay_count: overlayConfigs.length,
      });
      setCreatedAlbum(album);
      setStep(6);
      onCreated(album);
    } catch (err: unknown) {
      if (handleQuotaError(err)) return;
      const e = err as { response?: { data?: { error?: string } } };
      setSubmitError(e.response?.data?.error || 'Failed to create album');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Titles per step ─────────────────────────────────────────────────────────

  const titles: Record<WizardStep, string> = {
    1: 'New Album',
    2: 'Select Photos',
    3: 'Cover Photo',
    4: 'Face Recognition',
    5: 'Add Overlays',
    6: 'Album Created',
  };

  return (
  <>
    <Modal isOpen={isOpen} onClose={handleClose} title={titles[step]} size={step === 2 ? 'full' : 'xl'}>
      <div className={step === 2 ? styles['wizard-body-step2'] : styles['wizard-body']}>
        <StepDots step={step} />

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className={step === 2 ? styles['motion-step2'] : undefined}
            initial={{ opacity: 0, x: shouldReduceMotion ? 0 : 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: shouldReduceMotion ? 0 : -24 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.18, ease: 'easeOut' }}
          >
            {step === 1 && (
              <Step1
                name={name}
                description={description}
                allowedEmailDomains={allowedEmailDomains}
                onChange={(n, d) => { setName(n); setDescription(d); }}
                onDomainsChange={setAllowedEmailDomains}
                onNext={() => setStep(2)}
                onCancel={handleClose}
              />
            )}
            {step === 2 && (
              <Step2
                eventId={eventId}
                selectedIds={selectedPhotoIds}
                onToggle={togglePhoto}
                onRangeToggle={rangeTogglePhotos}
                onSelectAll={handleSelectAll}
                onPhotosLoaded={setLoadedPhotos}
                onNext={() => setStep(3)}
                onBack={() => setStep(1)}
              />
            )}
            {step === 3 && (
              <StepCover
                selectedPhotos={loadedPhotos.filter((p) => selectedPhotoIds.has(p._id))}
                coverPhotoId={coverPhotoId}
                onSetCover={setCoverPhotoId}
                onNext={() => setStep(4)}
                onBack={() => setStep(2)}
              />
            )}
            {step === 4 && (
              <FaceRecognitionStep
                config={faceConfig}
                onChange={(patch) => setFaceConfig((prev) => ({ ...prev, ...patch }))}
                isLive={false}
                onNext={() => setStep(5)}
                onBack={() => setStep(3)}
              />
            )}
            {step === 5 && (
              <Step3
                eventId={eventId}
                overlayConfigs={overlayConfigs}
                previewPhoto={previewPhoto}
                onAdd={addOverlay}
                onUpdate={updateOverlayConfig}
                onRemove={removeOverlayConfig}
                onSubmit={handleSubmit}
                onBack={() => setStep(4)}
                submitting={submitting}
                submitError={submitError}
                isOwner={isOwner}
                attachDefaultOverlay={attachDefaultOverlay}
                onToggleAttachDefault={setAttachDefaultOverlay}
                defaultBranded={defaultBranded}
                defaultAnchor={defaultAnchor}
              />
            )}
            {step === 6 && createdAlbum && (
              <Step4
                album={createdAlbum}
                event={event}
                coverPhoto={previewPhoto}
                overlayConfigs={overlayConfigs}
                onAlbumUpdated={setCreatedAlbum}
                onDone={handleClose}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </Modal>
    <QuotaLimitModal {...quotaModal} onClose={closeQuotaModal} />
  </>
  );
}
